<template>
    <div
        class="textbox-container"
        :class="[
            notifyType,
            variantClass,
            {
                focus: focusState,
                readonly: isReadonly,
                disabled: isDisabled,
                'has-input': hasInput,
            },
        ]"
    >
        <div class="textbox" @mousedown="onMouseDown" @click="onClick">
            <div class="text-box-icon">
                <search-icon :state="focusState" />
            </div>
            <div class="textbox-left">
                <div class="input-block">
                    <input
                        v-bind="$attrs"
                        :id="uniqueId"
                        ref="inputRef"
                        v-model="input"
                        :placeholder="resolvedPlaceholder"
                        type="text"
                        :required="required"
                        :pattern="pattern"
                        :minlength="minlength"
                        :class="{ bold: !focusState, hidden: !focusState && !input }"
                        :disabled="isDisabled"
                        @focus="onFocus"
                        @blur="onBlur"
                        @change="onChange"
                        @invalid="onInvalid"
                        @input="onInput"
                        @keyup.enter="onEnter"
                    />
                </div>
            </div>
            <div v-if="focusState" class="textbox-right">
                <svg
                    id="clear"
                    class="icon-close"
                    tabindex="-1"
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    @click="clear"
                >
                    <path
                        d="M15.667 8A7.666 7.666 0 1 1 8 .333 7.675 7.675 0 0 1 15.667 8zM8.943 8l2.717-2.717a.667.667 0 0 0-.943-.942L8 7.057 5.283 4.341a.666.666 0 1 0-.943.942L7.058 8 4.34 10.717a.667.667 0 1 0 .943.942L8 8.943l2.717 2.716a.666.666 0 0 0 .943-.942L8.943 8z"
                    />
                </svg>
            </div>
        </div>
        <alert v-if="notifyMessage" :size="ComponentVariant.Tucked" :variant="notifyType">
            <span>{{ notifyMessage }}</span>
        </alert>
    </div>
</template>

<script setup>
import { useI18n } from 'vue-i18n';
import { ref, computed } from 'vue';
import { usePalette, paletteProps, SearchIcon, Alert, useTextbox } from 'ihunter-pathfinder-components';
import { ComponentVariant } from '../composables/useConstants';
const { t } = useI18n();

const props = defineProps({
    ...paletteProps(),
    readonly: { type: Boolean, default: true },
    disabled: { type: Boolean, default: null },
    placeholder: { type: String, default: '' },
    type: { type: String, default: 'searchlist' }, // Accepts 'searchlist' or 'searchmap'
    required: { type: Boolean, default: false },
    pattern: { type: String, default: null },
    minlength: { type: Number, default: 0 },
    defaultMessage: { type: String, default: '' },
    successMessage: { type: String, default: '' },
    showInput: { type: Boolean, default: false },
});

const emit = defineEmits(['input', 'click', 'change', 'enter', 'focus', 'blur']);
const input = defineModel({ type: String, required: true });
const { colorPalette } = usePalette(props);

const resolvedPlaceholder = computed(() => {
    return props.placeholder ?? t('common.button.search');
});

const variantClass = computed(() => (props.type === 'searchmap' ? 'searchmap' : 'searchlist'));

const {
    inputRef,
    notifyMessage,
    notifyType,
    uniqueId,
    hasInput,
    focusState,
    isReadonly,
    isDisabled,
    onClick,
    onFocus,
    onBlur,
    onChange,
    onEnter,
    clear,
    doFocus,
    invalidate,
    onMouseDown,
    onInvalid,
    onInput,
} = useTextbox(props, emit, input);

defineExpose({ focus: doFocus, clear, invalidate });
</script>

<style lang="scss" scoped>
@use 'ihunter-pathfinder/styles' as Pathfinder;
@use 'ihunter-pathfinder/styles/textbox' as PathfinderBox;

.textbox-container {
    @include PathfinderBox.Textbox;
}
</style>
